import { useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Paper,
	ButtonGroup,
	Tooltip,
	Alert,
	Snackbar
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminApi, ExportFormat } from '@/services';
import { BasePageLayout } from '@/components/common';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';

/**
 * 3 bảng cho phép "kèm cột ID (UUID)" — khớp `TABLES_WITH_ID_COLUMN` phía
 * backend (`AdminExportService.ts`). `student` KHÔNG có mặt: `SinhVien` khoá
 * chính là `maSV`, không có cột `id`.
 */
const TABLES_WITH_ID_COLUMN = new Set(['truong_thpt', 'raw_truong_thpt', 'tinh']);

export const DataExportPage = () => {
	const [exporting, setExporting] = useState<string | null>(null);
	const [exportedBytes, setExportedBytes] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [includeIdByTable, setIncludeIdByTable] = useState<Record<string, boolean>>({});

	const { data, isLoading, error: loadError } = useQuery({
		queryKey: ['admin', 'exportable-tables'],
		queryFn: adminApi.getExportableTables,
		staleTime: 60000
	});

	const handleExport = async (tableKey: string, tableName: string, format: ExportFormat) => {
		setExporting(`${tableKey}-${format}`);
		setExportedBytes(0);
		setError(null);

		try {
			await adminApi.exportTable(tableKey, format, setExportedBytes, includeIdByTable[tableKey]);
			setSuccess(`Đã tải xuống ${tableName} (${format.toUpperCase()})`);
		} catch (err) {
			console.error('[DataExportPage] Export error:', err);
			setError(`Lỗi khi tải xuống ${tableName}: ${err instanceof Error ? err.message : 'Lỗi không xác định'}`);
		} finally {
			setExporting(null);
			setExportedBytes(0);
		}
	};

	// Server không gửi Content-Length (chunked) nên chỉ có số byte đã nhận, không có phần trăm.
	const formatBytes = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	if (isLoading) {
		return (
			<BasePageLayout>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
					<CircularProgress />
				</Box>
			</BasePageLayout>
		);
	}

	if (loadError) {
		return (
			<BasePageLayout>
				<Alert severity="error">
					Lỗi tải danh sách bảng: {loadError instanceof Error ? loadError.message : 'Lỗi không xác định'}
				</Alert>
			</BasePageLayout>
		);
	}

	return (
		<BasePageLayout>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
					Xuất dữ liệu
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Tải xuống toàn bộ dữ liệu của từng bảng dưới dạng Excel hoặc CSV
				</Typography>
			</Box>

			<Card>
				<CardContent>
					<TableContainer component={Paper} variant="outlined">
						<Table>
							<TableHead>
								<TableRow sx={{ bgcolor: 'action.hover' }}>
									<TableCell sx={{ fontWeight: 'bold' }}>STT</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Tên bảng</TableCell>
									<TableCell align="right" sx={{ fontWeight: 'bold' }}>Số bản ghi</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Mã bảng</TableCell>
									<TableCell align="center" sx={{ fontWeight: 'bold' }}>Tải xuống</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data?.tables.map((table, index) => (
									<TableRow key={table.key} hover>
										<TableCell>{index + 1}</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<TableChartIcon fontSize="small" color="primary" />
												<Typography fontWeight="medium">{table.name}</Typography>
											</Box>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight="medium">
												{table.count?.toLocaleString('vi-VN') ?? '—'}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												sx={{
													fontFamily: 'monospace',
													bgcolor: 'action.hover',
													px: 1,
													py: 0.5,
													borderRadius: 1,
													display: 'inline-block'
												}}
											>
												{table.key}
											</Typography>
										</TableCell>
										<TableCell align="center">
											{exporting?.startsWith(`${table.key}-`) && exportedBytes > 0 && (
												<Typography variant="caption" color="text.secondary" display="block">
													Đã tải {formatBytes(exportedBytes)}...
												</Typography>
											)}
											{TABLES_WITH_ID_COLUMN.has(table.key) && (
												<FormControlLabel
													sx={{ display: 'block', mb: 0.5, mr: 0 }}
													control={
														<Checkbox
															size="small"
															checked={!!includeIdByTable[table.key]}
															disabled={exporting !== null}
															onChange={(e) =>
																setIncludeIdByTable((prev) => ({ ...prev, [table.key]: e.target.checked }))
															}
														/>
													}
													label={<Typography variant="caption">Kèm cột ID (UUID)</Typography>}
												/>
											)}
											<ButtonGroup size="small" variant="outlined">
												<Tooltip title="Tải xuống file Excel (.xlsx)">
													<Button
														onClick={() => handleExport(table.key, table.name, 'excel')}
														disabled={exporting !== null}
														startIcon={
															exporting === `${table.key}-excel` ? (
																<CircularProgress size={16} />
															) : (
																<DescriptionIcon />
															)
														}
														color="success"
													>
														Excel
													</Button>
												</Tooltip>
												<Tooltip title="Tải xuống file CSV (.csv)">
													<Button
														onClick={() => handleExport(table.key, table.name, 'csv')}
														disabled={exporting !== null}
														startIcon={
															exporting === `${table.key}-csv` ? (
																<CircularProgress size={16} />
															) : (
																<DownloadIcon />
															)
														}
														color="info"
													>
														CSV
													</Button>
												</Tooltip>
											</ButtonGroup>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</CardContent>
			</Card>

			<Snackbar
				open={!!error}
				autoHideDuration={6000}
				onClose={() => setError(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert severity="error" onClose={() => setError(null)}>
					{error}
				</Alert>
			</Snackbar>

			<Snackbar
				open={!!success}
				autoHideDuration={3000}
				onClose={() => setSuccess(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert severity="success" onClose={() => setSuccess(null)}>
					{success}
				</Alert>
			</Snackbar>
		</BasePageLayout>
	);
};
